import { isApiRequestConfig } from '../utils/apiAuthRecovery';
import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseAuth';
import { createApiAuthResponseErrorHandler, API_SESSION_EXPIRED_EVENT } from '../utils/apiAuthRecovery';
import { getAuthHeaders } from '../utils/authHeaders';
import { AUTH_STAGE_TIMEOUT_MS, runAuthStage } from '../utils/authTelemetry';
import { AgentPlatformApiError, GoogleCapability, GoogleConnectedAccount, GoogleConnectionDomain } from './api';
import { resolveGoogleConnectionsApiOrigin } from './googleConfiguration';

const origin = resolveGoogleConnectionsApiOrigin();
const apiBase = origin ? `${origin}/api/agent-platform/google` : '';
const client = axios.create({ baseURL: apiBase, withCredentials: true });

client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (!isApiRequestConfig(config, apiBase)) throw new Error('Request target is outside the configured API.');
  const headers = AxiosHeaders.from(config.headers);
  const authHeaders = await getAuthHeaders(auth.currentUser);
  Object.entries(authHeaders).forEach(([key, value]) => headers.set(key, value));
  config.headers = headers;
  return config;
});
client.interceptors.response.use(response => response, createApiAuthResponseErrorHandler({
  apiBase,
  getCurrentUser: () => auth.currentUser,
  replayRequest: config => client.request(config as InternalAxiosRequestConfig),
  setAuthorizationHeader: (config, token) => {
    const headers = AxiosHeaders.from(config.headers || {}); headers.set('Authorization', `Bearer ${token}`); config.headers = headers;
  },
  terminateSession: async () => {
    window.dispatchEvent(new CustomEvent(API_SESSION_EXPIRED_EVENT));
    await runAuthStage('session_sign_out', () => signOut(auth), { timeoutMs: AUTH_STAGE_TIMEOUT_MS.signOut });
  },
}));

async function request<T>(path: string, method: 'get' | 'post' = 'get', data?: unknown, signal?: AbortSignal): Promise<T> {
  if (!apiBase) throw new AgentPlatformApiError(0, 'Google connections are not configured for this dashboard.');
  try {
    const response = method === 'get' ? await client.get<T>(path, { signal }) : await client.post<T>(path, data, { signal });
    return response.data;
  } catch (error: any) {
    if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') throw error;
    if (error instanceof AgentPlatformApiError) throw error;
    throw new AgentPlatformApiError(error?.response?.status || 0, error?.response?.data?.detail || 'The Google connection request could not be completed.');
  }
}

export const googleConnectionsApi = {
  accounts: (workspaceId: string, signal?: AbortSignal) => request<GoogleConnectedAccount[]>(`/connections?workspace_id=${encodeURIComponent(workspaceId)}`, 'get', undefined, signal),
  start: (workspaceId: string, capabilities: GoogleCapability[], domain: GoogleConnectionDomain, signal?: AbortSignal) => request<{ authorizationUrl: string; expiresAt: string }>('/connections/start', 'post', { workspace_id: workspaceId, capabilities, domain, return_route_key: 'connections', force_consent: false }, signal),
  disconnect: (workspaceId: string, accountId: string, signal?: AbortSignal) => request<GoogleConnectedAccount>(`/connections/${encodeURIComponent(accountId)}/disconnect`, 'post', { workspace_id: workspaceId }, signal),
};
